import { useMemo } from 'react';

import type { CodeString } from '@/consts/codes';
import { buildRouteMapping, findMatchingRoute } from '@/features/header/utils/breadcrumbs';
import { useDynamicMenu } from '@/features/navigation/hooks/useDynamicMenu';
import type { TranslationKey } from '@/i18n/i18n';
import { useLocation } from 'react-router';

interface Breadcrumb {
  label: TranslationKey;
  route?: string;
  code?: CodeString;
  isClickable?: boolean;
}

const DYNAMIC_SECTIONS = new Set(['navigation.favorites.title', 'navigation.recent.title']);

export const useBreadcrumbs = (): Breadcrumb[] => {
  const location = useLocation();
  const { fullMenu } = useDynamicMenu();

  // Use fullMenu (includes hidden items) so direct URL navigation to a hidden route still resolves breadcrumbs.
  // Exclude favorites/recents — they duplicate routes with wrong parent chains.
  const allRoutes = useMemo(() => {
    const sections = fullMenu.filter((section) => !DYNAMIC_SECTIONS.has(section.text));
    return buildRouteMapping(sections);
  }, [fullMenu]);

  return useMemo(() => {
    const fullPath = location.pathname + location.search;
    const active = findMatchingRoute(fullPath, allRoutes);
    if (!active) return [];

    const breadcrumbs: Breadcrumb[] = [];

    if (active.parentChain && active.parentChain.length > 0) {
      for (const parentLabel of active.parentChain) {
        const parentRoute = allRoutes.find((r) => r.label === parentLabel);
        breadcrumbs.push({
          label: parentLabel,
          route: parentRoute?.route,
          code: parentRoute?.code,
          isClickable: !!(parentRoute?.route && !parentRoute.isRedirectOnly)
        });
      }
    }

    breadcrumbs.push({
      label: active.label,
      route: active.route,
      code: active.code,
      isClickable: false
    });

    return breadcrumbs;
  }, [location.pathname, location.search, allRoutes]);
};
